import { faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Field, FieldArray, Form, Formik, FormikProps } from "formik";
import { Ref, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { handleError } from "../common/utils";
import { useExternalDataService } from "../services/external-data.service";
import { ExternalData, ExternalDataField } from "../types/external-data.types";
import {
  RegisteredVoterFlags,
  ValidationForm,
  WorkDto,
  WorkField,
} from "../types/work.types";
import { InlineSpinner } from "./inline-spinner";

interface FieldValidationProps {
  work: WorkDto;
  formikRef: Ref<FormikProps<ValidationForm>> | null;
  onNext?: (
    fields: WorkField[],
    externalDataRecordId: string | null,
    registeredVoterFlags: RegisteredVoterFlags,
    searchParams?: Record<string, string>
  ) => void;
  nextLabel: string | null;
  onRestoreSearchParams?: (restoreCallback: (params: Record<string, string>) => void) => void;
}

export default function VoterValidation({
  work,
  formikRef,
  onNext,
  nextLabel,
  onRestoreSearchParams,
}: FieldValidationProps) {
  const { getWorkVoter, voterRegistrationSearch } = useExternalDataService();
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [voterRegistrations, setVoterRegistrations] = useState<
    ExternalData[] | null
  >(null);
  const [totalHits, setTotalHits] = useState(0);
  const [searchFieldValues, setSearchFieldValues] = useState<Record<string, string>>({});

  const mountedRef = useRef(true);
  const hasLoadedResultsRef = useRef(false);
  const selectedExternalDataRecordIdRef = useRef<string>("");
  const pendingRestoreRef = useRef<{
    voterId?: string;
    flags?: RegisteredVoterFlags;
  } | null>(null);
  const restoreCallbackRef = useRef<((params: Record<string, string>) => void) | null>(null);
  const isRestoringRef = useRef(false);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // After search results load, restore pending voter selection if it exists in results
  useEffect(() => {
    if (voterRegistrations && voterRegistrations.length > 0 && pendingRestoreRef.current && formikRef) {
      const { voterId, flags } = pendingRestoreRef.current;

      // Check if the pending voter ID exists in the search results
      const voterExists = voterRegistrations.some(v => v.id.toString() === voterId);

      if (voterExists && voterId) {
        const formikInstance = 'current' in formikRef ? formikRef.current : (formikRef as any);
        if (formikInstance) {
          formikInstance.setFieldValue("externalDataRecordId", voterId);
          formikInstance.setFieldValue("registeredVoterFlags", flags ?? RegisteredVoterFlags.Valid);
          selectedExternalDataRecordIdRef.current = voterId;
          pendingRestoreRef.current = null;
        }
      }
    }
  }, [voterRegistrations, formikRef]);

  const workFields = useMemo(
    () => work.fields.map((field, index) => ({ field, index })),
    [work.fields]
  );

  const fieldValuesKey = useMemo(
    () => work.fields.map(f => `${f.id}:${f.value}`).join("|"),
    [work.fields]
  );

  const addressFields = useMemo(() => {
    const addressField = workFields.find(({ field }) =>
      field.name?.toLowerCase().includes("address")
    );
    return addressField ? [addressField] : [];
  }, [workFields]);

  const nameFields = useMemo(() => {
    const fields = workFields.filter((fi) => {
      const loweredName = fi.field.name?.toLowerCase();
      return (
        fi.field.canBeInvalid &&
        (loweredName.includes("name") ||
          loweredName.includes("first") ||
          loweredName.includes("last"))
      );
    });
    return fields;
  }, [workFields]);

  const searchFields = useMemo(() => {
    return [...nameFields, ...addressFields];
  }, [nameFields, addressFields]);

  const handleSearch = useCallback(
    (fields: WorkField[], setFieldValue) => {
      var searchFieldNames = searchFields.map((fi) => fi.field.name);
      const externalDataFields = fields.filter((f) =>
        searchFieldNames.includes(f.name)
      );

      if (work?.workId) {
        setErrorMessage("");
        setLoading(true);
        voterRegistrationSearch(
          work.workId.toString(),
          externalDataFields
        ).then(
          (externalData) => {
            if (!mountedRef.current) {
              return;
            }
            setLoading(false);
            setVoterRegistrations(externalData.results);
            console.log(`Search results`, externalData);
            setFieldValue("voterRegistrations", externalData.results);
            setTotalHits(externalData.totalHits);
          },
          (error) => {
            if (!mountedRef.current) {
              return;
            }
            handleError(error, setErrorMessage, setLoading);
          }
        );
      }
    },
    [searchFields, work.workId, voterRegistrationSearch]
  );

  const prevFieldValuesKeyRef = useRef(fieldValuesKey);
  useEffect(() => {
    if (prevFieldValuesKeyRef.current !== fieldValuesKey) {
      prevFieldValuesKeyRef.current = fieldValuesKey;
      hasLoadedResultsRef.current = false;
      setSearchFieldValues({});
    }
  }, [fieldValuesKey]);

  useEffect(() => {
    if (!work?.workId) {
      return;
    }

    // Skip loading selected voter if we're in the middle of a restoration
    // The restoration callback will load search results
    if (isRestoringRef.current) {
      return;
    }

    if (work.externalDataRecordId) {
      // A voter has been selected in a previous session, load their details
      getWorkVoter(work.workId, work.externalDataRecordId.toString()).then(
        (externalData) => {
          if (!mountedRef.current) {
            return;
          }
          setVoterRegistrations([externalData]);

          // Also update the Formik form to select this voter in the UI
          if (formikRef && work.externalDataRecordId) {
            const formikInstance = 'current' in formikRef ? formikRef.current : (formikRef as any);
            if (formikInstance) {
              formikInstance.setFieldValue('externalDataRecordId', work.externalDataRecordId.toString());
            }
          }
        },
        (error) => {
          if (!mountedRef.current) {
            return;
          }
          handleError(error, setErrorMessage, setLoading);
        }
      );
    } else if (!hasLoadedResultsRef.current) {
      // Only run auto-search once per work item load if no results are already loaded
      // Use a ref to track whether we've already loaded results
      // This prevents the search from being re-triggered when parent re-renders (validation warnings, etc.)
      const hasSearchFieldValue = searchFields.find((fi) => fi.field.value);

      if (hasSearchFieldValue) {
        hasLoadedResultsRef.current = true;
        const externalDataFields = searchFields.map((fi) => ({
          name: fi.field.name,
          value: fi.field.value,
        }));

        setErrorMessage("");
        setLoading(true);

        voterRegistrationSearch(work.workId.toString(), externalDataFields).then(
          (externalData) => {
            if (!mountedRef.current) {
              return;
            }
            setLoading(false);
            setVoterRegistrations(externalData.results);
            setTotalHits(externalData.totalHits);
          },
          (error) => {
            if (!mountedRef.current) {
              return;
            }
            handleError(error, setErrorMessage, setLoading);
          }
        );
      }
    }
  // Dependency array:
  // - work?.workId: When a new work item is loaded (triggers hasLoadedResultsRef reset)
  // - work?.externalDataRecordId: When selected voter changes
  // Excluded: searchFields, voterRegistrations - Prevent re-triggering on parent re-renders
  // The effect will run on initial load and only perform a search once per work item
  // This prevents the search from being triggered by parent re-renders during validation warnings
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [work?.workId, work?.externalDataRecordId, fieldValuesKey]);

  const handleNext = (formValues: ValidationForm) => {
    // Pass back the original work.fields, not the edited form values
    // The search field edits are only for voter lookup, not for updating the work
    // Only the voter registration selection (externalDataRecordId) should be updated
    // Also pass searchFieldValues so they can be sent to backend and echoed back
    // Include the selected voter ID and flags so they can be restored if validation warning occurs
    const searchParamsWithVoterId: Record<string, string> = {
      ...searchFieldValues,
    };

    // Only include selectedVoterId if a voter is actually selected (not "None of these")
    if (formValues.externalDataRecordId) {
      searchParamsWithVoterId.__selectedVoterId = formValues.externalDataRecordId;
      searchParamsWithVoterId.__selectedVoterFlags = formValues.registeredVoterFlags.toString();
    }

    return onNext?.(
      work.fields,
      formValues.externalDataRecordId ?? null,
      formValues.registeredVoterFlags,
      searchParamsWithVoterId
    );
  };

  // Set up the restoration callback for when validation warning occurs
  useEffect(() => {
    // Create the restoration callback that will be called when parent detects validation warning
    const restoreCallback = (params: Record<string, string>) => {
      // Set flag to indicate we're in restoration mode
      isRestoringRef.current = true;

      // Extract the selected voter ID and flags if present
      const { __selectedVoterId, __selectedVoterFlags, ...searchParams } = params;

      // Get the Formik instance to update field values
      if (formikRef) {
        const formikInstance = 'current' in formikRef ? formikRef.current : (formikRef as any);
        if (formikInstance) {
          // Update local search field values state
          setSearchFieldValues(searchParams);

          // Create updated fields array with restored values
          const updatedFields = formikInstance.values.fields.map((field: WorkField, index: number) => {
            const searchField = searchFields.find(sf => sf.index === index);
            if (searchField) {
              const fieldName = searchField.field.name;
              const restoredValue = searchParams[fieldName] ?? field.value ?? "";
              return { ...field, value: restoredValue };
            }
            return field;
          });

          // Also update Formik's field values
          searchFields.forEach((fi) => {
            const fieldName = fi.field.name;
            const restoredValue = searchParams[fieldName] ?? fi.field.value ?? "";
            formikInstance.setFieldValue(`fields[${fi.index}].value`, restoredValue);
          });

          // Store the voter ID and flags to restore after search completes
          if (__selectedVoterId) {
            const voterFlags = __selectedVoterFlags ? parseInt(__selectedVoterFlags) : RegisteredVoterFlags.Valid;
            pendingRestoreRef.current = {
              voterId: __selectedVoterId,
              flags: voterFlags
            };
          }

          // Trigger search with the updated fields array (constructed above with restored values)
          handleSearch(updatedFields, formikInstance.setFieldValue);

          // Clear the restoring flag after a short delay
          setTimeout(() => {
            isRestoringRef.current = false;
          }, 100);
        }
      }
    };

    // Store the callback in ref so parent can access it
    restoreCallbackRef.current = restoreCallback;

    // If parent is already expecting to use this callback, call onRestoreSearchParams with it
    if (onRestoreSearchParams) {
      onRestoreSearchParams(restoreCallback);
    }
  }, [onRestoreSearchParams, searchFields, handleSearch, formikRef]);

  const getFieldValues = (
    fields: ExternalDataField[],
    subset: { field: ExternalDataField; index: number }[]
  ) => {
    const values: string[] = [];
    for (const field of fields) {
      if (subset.map((fi) => fi.field.name).includes(field.name)) {
        values.push(field.value);
      }
    }
    return values.join(" ");
  };

  const initialValues = useMemo(
    () => ({
      fields: work.fields,
      externalDataRecordId: work.externalDataRecordId ?? "",
      // Use the work's registeredVoterFlags if it has a meaningful value (not Unknown),
      // regardless of whether a voter was selected (e.g., "Not Registered" with no voter match).
      // Only default to Unknown when the work has no flag set yet.
      registeredVoterFlags: work.registeredVoterFlags
        ? work.registeredVoterFlags
        : (work.externalDataRecordId ? RegisteredVoterFlags.Valid : RegisteredVoterFlags.Unknown),
      voterRegistrations: [],
      totalHits: 0,
    } as ValidationForm),
    [work.workId, work.fields, work.externalDataRecordId, work.registeredVoterFlags]
  );


  return (
    <div className="row mt-3">
      <Formik
        innerRef={formikRef}
        enableReinitialize={false}
        initialValues={initialValues}
        validate={(values) => {
          const errors = {} as any;
          if (
            values.registeredVoterFlags === RegisteredVoterFlags.Valid &&
            values.externalDataRecordId === ""
          ) {
            errors.fieldName =
              "A valid voter registration must have a selected voter";
          }
          return errors;
        }}
        onSubmit={handleNext}
      >
        {({ values, isSubmitting, setFieldValue }) => {
          return (
          <Form>
            <div className="row mb-2">
              {searchFields.map((fi) => (
                <div className="col form-group" key={fi.field?.id}>
                  <label
                    htmlFor={fi.field?.name}
                    style={{ whiteSpace: "nowrap" }}
                  >
                    {fi.field?.name}
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder={`Enter ${fi.field?.name}`}
                    value={searchFieldValues[fi.field.name] ?? (fi.field.value || "")}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      setSearchFieldValues(prev => ({
                        ...prev,
                        [fi.field.name]: newValue
                      }));
                      // Also update Formik's field value so validation works
                      setFieldValue(`fields[${fi.index}].value`, newValue);
                    }}
                  />
                </div>
              ))}
            </div>

            <div className="row mb-2">
              <div className="col-sm-4">
                <button
                  type="button"
                  className="btn btn-sw"
                  onClick={() => {
                    handleSearch(values.fields, setFieldValue);
                  }}
                  disabled={loading}
                >
                  {loading && <InlineSpinner />}
                  Search
                </button>
              </div>
              <div className="col-sm-8 pt-2 text-end">
                {totalHits > 0 && (
                  <span className="text-muted">
                    Showing {voterRegistrations?.length} of {totalHits} possible
                    matches
                  </span>
                )}
              </div>
            </div>
            <div className="row mb-2">
              <div className="col">
                <FieldArray name="voterRegistrations">
                  {() => (
                    <div className="border-left border-bottom border-right ps-2 rounded">
                      <div
                        style={{
                          maxHeight: 125,
                          overflowY: "auto",
                          overflowX: "hidden",
                        }}
                      >
                        {voterRegistrations &&
                          voterRegistrations.length > 0 && (
                            <div className="row" key="voterRegistration-none">
                              <div className="col-sm-6">
                                <div className="form-group">
                                  <div className="form-check form-check-inline">
                                    <Field
                                      id="externalDataRecordId-0"
                                      className="form-check-input"
                                      type="radio"
                                      name="externalDataRecordId"
                                      required
                                      onChange={() => {
                                        selectedExternalDataRecordIdRef.current = "";
                                        setFieldValue(
                                          "externalDataRecordId",
                                          ""
                                        );
                                      }}
                                      checked={
                                        values.externalDataRecordId === ""
                                      }
                                    />
                                    <label
                                      className="m-0"
                                      htmlFor="externalDataRecordId-0"
                                    >
                                      None of these
                                    </label>
                                  </div>
                                </div>
                              </div>
                              <div className="col-sm-6"></div>
                            </div>
                          )}

                        {voterRegistrations &&
                          voterRegistrations.length > 0 &&
                          voterRegistrations?.map((voterRegistration) => (
                            <div
                              className="row"
                              key={`voterRegistration-${voterRegistration.id}`}
                            >
                              <div className="col-sm-6">
                                <div className="form-check form-check-inline">
                                  <Field
                                    id={`externalDataRecordId-${voterRegistration.id}`}
                                    className="form-check-input"
                                    type="radio"
                                    name="externalDataRecordId"
                                    required
                                    value={voterRegistration.id.toString()}
                                    checked={values.externalDataRecordId === voterRegistration.id.toString()}
                                    onChange={() => {
                                      const voterId = voterRegistration.id.toString();
                                      selectedExternalDataRecordIdRef.current = voterId;
                                      setFieldValue(
                                        "externalDataRecordId",
                                        voterId
                                      );
                                      setFieldValue(
                                        "registeredVoterFlags",
                                        RegisteredVoterFlags.Valid
                                      );
                                    }}
                                  />
                                  <label
                                    className="m-0"
                                    htmlFor={`externalDataRecordId-${voterRegistration.id}`}
                                  >
                                    {getFieldValues(
                                      voterRegistration.fields,
                                      nameFields
                                    )}
                                  </label>
                                </div>
                              </div>
                              <div className="col-sm-6">
                                <label
                                  className="m-0"
                                  htmlFor={`externalDataRecordId-${voterRegistration.id}`}
                                >
                                  {getFieldValues(
                                    voterRegistration.fields,
                                    addressFields
                                  )}
                                </label>
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </FieldArray>
              </div>
            </div>

            <div className="row mb-2">
              <div className="col-sm-3">
                <div className="form-group">
                  <div className="form-check form-check-inline">
                    <Field
                      id="noMismatch"
                      className="form-check-input"
                      type="radio"
                      name="registeredVoterFlags"
                      required
                      onChange={() => {
                        setFieldValue(
                          "registeredVoterFlags",
                          RegisteredVoterFlags.Valid
                        );
                      }}
                      checked={
                        values.registeredVoterFlags ===
                        RegisteredVoterFlags.Valid
                      }
                    />
                    <label className="m-0" htmlFor="noMismatch">
                      Valid/No Mismatch
                    </label>
                  </div>
                </div>
              </div>

              <div className="col-sm-3">
                <div className="form-group">
                  <div className="form-check form-check-inline">
                    <Field
                      id="mismatchedName"
                      className="form-check-input"
                      type="radio"
                      name="registeredVoterFlags"
                      required
                      onChange={() => {
                        setFieldValue(
                          "registeredVoterFlags",
                          RegisteredVoterFlags.MismatchedName
                        );
                      }}
                      checked={
                        values.registeredVoterFlags ===
                        RegisteredVoterFlags.MismatchedName
                      }
                    />
                    <label className="m-0" htmlFor="mismatchedName">
                      Mismatched Name
                    </label>
                  </div>
                </div>
              </div>

              <div className="col-sm-3">
                <div className="form-group">
                  <div className="form-check form-check-inline">
                    <Field
                      id="mismatchedAddress"
                      className="form-check-input"
                      type="radio"
                      name="registeredVoterFlags"
                      required
                      onChange={() => {
                        setFieldValue(
                          "registeredVoterFlags",
                          RegisteredVoterFlags.MismatchedAddress
                        );
                      }}
                      checked={
                        values.registeredVoterFlags ===
                        RegisteredVoterFlags.MismatchedAddress
                      }
                    />
                    <label className="m-0" htmlFor="mismatchedAddress">
                      Mismatched Address
                    </label>
                  </div>
                </div>
              </div>

              <div className="col-sm-3">
                <div className="form-group">
                  <div className="form-check form-check-inline">
                    <Field
                      id="notRegistered"
                      className="form-check-input"
                      type="radio"
                      name="registeredVoterFlags"
                      required
                      onChange={() => {
                        setFieldValue(
                          "registeredVoterFlags",
                          RegisteredVoterFlags.NotRegistered
                        );
                      }}
                      checked={
                        values.registeredVoterFlags ===
                        RegisteredVoterFlags.NotRegistered
                      }
                    />
                    <label className="m-0" htmlFor="notRegistered">
                      Not Registered
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {errorMessage && (
              <div className="row mt-1">
                <div className="col">
                  <div className="form-group">
                    <div className="alert alert-danger" role="alert">
                      {errorMessage}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="row text-center">
              <div className="col-sm-12 mt-1">
                <div className="form-group">
                  <button
                    type="submit"
                    className="btn btn-secondary"
                    disabled={isSubmitting}
                  >
                    {!isSubmitting && (
                      <FontAwesomeIcon icon={faArrowRight} className="me-2" />
                    )}
                    {isSubmitting && <InlineSpinner />}
                    {nextLabel || "Next"}
                  </button>
                </div>
              </div>
            </div>
          </Form>
        );
        }}
      </Formik>
    </div>
  );
}
